/**
 * Auth tokens live in a cookie scoped to the parent domain — not per-origin
 * localStorage — so that logging into any Lion Pro app (pro-web, the
 * certification portal, and any future app under the same domain) signs you
 * into all of them, and logging out of one logs you out of all. Same
 * approach Google uses across gmail/drive/docs/photos (a cookie scoped to
 * .google.com).
 *
 * Not HttpOnly: the app decodes the JWT client-side for roles/permissions
 * (see route-protection.tsx), so JS needs to read it. This is the same XSS
 * exposure profile as the previous localStorage-based approach — not
 * better, not worse. A fully HttpOnly cookie would be more secure but
 * requires every permission check to become a network round-trip instead
 * of a local decode.
 *
 * Domain resolution:
 *  - *.lionenergy.com (prod/dev/stage) -> .lionenergy.com, so the cookie is
 *    shared across the pro/certification/account subdomains.
 *  - *.localhost (pro.localhost / account.localhost / …) -> .localhost so the
 *    cookie is shared across those local subdomain apps.
 *  - plain localhost / 127.0.0.1 -> no Domain attribute (host-only). Cookies
 *    are not scoped by port (RFC 6265 §8.5), so pro :5173 / account :5174 /
 *    certification :5181 on the same hostname still share the cookie.
 *    localhost is also Google's OAuth redirect-URI exception, so this keeps
 *    SSO working locally without HTTPS when using plain localhost ports.
 */

function getCookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const { hostname } = window.location;
  if (hostname.endsWith(".lionenergy.com")) return ".lionenergy.com";
  if (hostname.endsWith(".localhost")) return ".localhost";
  return null;
}

const THIRTY_DAYS_SECONDS = 30 * 24 * 60 * 60;
/** Common browser limit for a single cookie (name + value + attributes). */
const COOKIE_BUDGET_CHARS = 4096;
const CHUNK_VALUE_CHARS = 3500;
const MAX_CHUNKS = 4;
const CHUNK_MARKER_PREFIX = "chunked:";

function chunkName(name: string, index: number): string {
  return `${name}__${index}`;
}

function readRawCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? match[1]! : null;
}

function cookieAttributes(maxAgeSeconds: number): string {
  const domain = getCookieDomain();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domainAttr = domain ? `; Domain=${domain}` : "";
  return `${domainAttr}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function parseChunkCount(raw: string | null): number {
  if (!raw?.startsWith(CHUNK_MARKER_PREFIX)) return 0;
  const count = Number(raw.slice(CHUNK_MARKER_PREFIX.length));
  return Number.isInteger(count) && count > 0 && count <= MAX_CHUNKS ? count : 0;
}

function clearChunks(name: string): void {
  for (let i = 0; i < MAX_CHUNKS; i++) {
    if (readRawCookie(chunkName(name, i)) !== null) {
      document.cookie = `${chunkName(name, i)}=${cookieAttributes(0)}`;
    }
  }
}

export function getAuthCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const raw = readRawCookie(name);
  if (raw === null) return null;
  const chunkCount = parseChunkCount(raw);
  if (chunkCount === 0) return decodeURIComponent(raw);

  let joined = "";
  for (let i = 0; i < chunkCount; i++) {
    const part = readRawCookie(chunkName(name, i));
    if (part === null) return null;
    joined += part;
  }
  return decodeURIComponent(joined);
}

export function setAuthCookie(name: string, value: string, maxAgeSeconds = THIRTY_DAYS_SECONDS): void {
  if (typeof document === "undefined") return;
  const attributes = cookieAttributes(maxAgeSeconds);
  const encoded = encodeURIComponent(value);
  const single = `${name}=${encoded}${attributes}`;

  if (single.length <= COOKIE_BUDGET_CHARS) {
    clearChunks(name);
    document.cookie = single;
    return;
  }

  const parts: string[] = [];
  for (let i = 0; i < encoded.length; i += CHUNK_VALUE_CHARS) {
    parts.push(encoded.slice(i, i + CHUNK_VALUE_CHARS));
  }
  if (parts.length > MAX_CHUNKS) {
    console.error(
      `[auth] Cookie "${name}" value is ${encoded.length} chars, over the ${MAX_CHUNKS * CHUNK_VALUE_CHARS}-char chunked limit; the session cannot be stored.`,
    );
    clearAuthCookie(name);
    return;
  }

  clearChunks(name);
  parts.forEach((part, i) => {
    document.cookie = `${chunkName(name, i)}=${part}${attributes}`;
  });
  document.cookie = `${name}=${CHUNK_MARKER_PREFIX}${parts.length}${attributes}`;
}

export function clearAuthCookie(name: string): void {
  if (typeof document === "undefined") return;
  clearChunks(name);
  document.cookie = `${name}=${cookieAttributes(0)}`;
}
